/**
 * SizingRenderer2DSlice — 2D oblique (centerline slice) strategy for the sizing display object.
 *
 * Manages:
 * - Contour polygon overlay (from current node's contour points)
 * - Diameter line overlay at current node (with tick marks and label)
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

import type { CenterlineNode } from '../../domain/centerline';
import type { DiameterEdit } from '../../domain/sizing/diameter-edit';
import type { Vec3 } from '../../domain/types';
import type { IViewport } from '../../viewports';
import { ContourOverlay } from '../overlay-actors/contour-overlay';
import { DiameterOverlay } from '../overlay-actors/diameter-overlay';
import type { SizingDisplayData, SizingDisplayProperties } from '../types';
import type { ISizingRenderer } from './sizing-display-object';

export class SizingRenderer2DSlice implements ISizingRenderer {
    private viewport: IViewport | null = null;
    private featureId: string = '';

    private contourOverlay: ContourOverlay | null = null;
    private diameterOverlay: DiameterOverlay | null = null;

    getDiameterOverlayId(): string {
        return `sizing-2d-diameter-${this.featureId}`;
    }

    initialize(viewport: IViewport, featureId: string): void {
        this.viewport = viewport;
        this.featureId = featureId;
        const overlayLayer = viewport.getOverlayLayer();

        this.contourOverlay = new ContourOverlay({
            id: `sizing-2d-contour-${featureId}`,
            contourPoints: [],
            color: 'rgba(102,204,255,1.0)',
            lineWidth: 1,
        });
        overlayLayer.addOverlayObject(this.contourOverlay);

        this.diameterOverlay = new DiameterOverlay({
            id: `sizing-2d-diameter-${featureId}`,
            p1: [0, 0, 0],
            p2: [0, 0, 0],
            value: 0,
            label: 'D',
            color: 'rgba(102,204,255,1.0)',
            edited: false,
        });
        this.diameterOverlay.interactive = true;
        this.diameterOverlay.visible = false;
        overlayLayer.addOverlayObject(this.diameterOverlay);
    }

    update(data: SizingDisplayData, _properties: SizingDisplayProperties): void {
        if (!this.viewport) return;

        const { currentNodeId, nodePool, diameterEdits } = data;

        if (currentNodeId === null) {
            this.hideOverlays();
            return;
        }

        const node = nodePool.get(currentNodeId);
        if (!node) {
            this.hideOverlays();
            return;
        }

        this.updateContour(node);
        this.updateDiameter(node, diameterEdits);
    }

    dispose(): void {
        if (this.viewport) {
            const overlayLayer = this.viewport.getOverlayLayer();
            if (this.contourOverlay) overlayLayer.removeOverlayObject(this.contourOverlay.id);
            if (this.diameterOverlay) overlayLayer.removeOverlayObject(this.diameterOverlay.id);
        }
        this.contourOverlay = null;
        this.diameterOverlay = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        return [];
    }

    // ========================================================================
    // Private
    // ========================================================================

    private updateContour(node: CenterlineNode): void {
        if (!this.contourOverlay) return;

        if (node.contour.length >= 3) {
            this.contourOverlay.setContourPoints(node.contour);
            this.contourOverlay.visible = true;
        } else {
            this.contourOverlay.visible = false;
        }
    }

    private updateDiameter(node: CenterlineNode, diameterEdits: ReadonlyMap<number, DiameterEdit>): void {
        if (!this.diameterOverlay) return;

        const edit = diameterEdits.get(node.id);
        let p1: Vec3;
        let p2: Vec3;
        let value: number;
        let edited: boolean;

        if (edit) {
            p1 = edit.p1;
            p2 = edit.p2;
            value = edit.editedValue;
            edited = true;
        } else {
            p1 = node.diameter.p1;
            p2 = node.diameter.p2;
            value = node.diameter.value;
            edited = false;
        }

        this.diameterOverlay.updateDiameter(p1, p2, value, edited);
        this.diameterOverlay.visible = true;
    }

    private hideOverlays(): void {
        if (this.contourOverlay) this.contourOverlay.visible = false;
        if (this.diameterOverlay) this.diameterOverlay.visible = false;
    }
}
